/**
 * Tag Rule Tab Content - Rules list, bulk import, system tags (list only).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Plus, Tag } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { Button } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { LoadingSpinner } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import {
  useTagRulesForTab,
  useCreateTagRuleForTab,
  useUpdateTagRuleForTab,
  useDeleteTagRuleForTab,
  useBulkDeleteTagRulesForTab,
  useReorderTagRulesForTab,
  type CreateListTagRuleDto,
  type ListTagRule,
  type TagRuleTabVariant,
} from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import {
  overridesInUse,
  TagRuleScopeBar,
  useTagRuleScope,
} from '@/features/tag-rule-scope';
import { HourTagsNotice, HourTagsPreview } from '@/features/hour-tags';

import { BulkImportSectionVariant } from './BulkImportSectionVariant';
import { RulesToolbar } from './RulesToolbar';
import { ListTagRuleFormModal } from '@/pages/admin/ListTagRulesPage/components/ListTagRuleFormModal';
import { SortableRuleRow } from '@/pages/admin/ListTagRulesPage/components/SortableRuleRow';
import { SystemTagsSection } from '@/pages/admin/ListTagRulesPage/components/SystemTagsSection';
import { VariantCacheSection } from '@/pages/admin/ListTagRulesPage/components/VariantCacheSection';
import { UNGROUPED_KEY } from '@/pages/admin/ListTagRulesPage/constants';
import { groupRules, rulesToBulkImportJson } from '@/pages/admin/ListTagRulesPage/utils';
interface TagRuleTabContentProps {
  variant: TagRuleTabVariant;
}

export function TagRuleTabContent({ variant }: TagRuleTabContentProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { success: toastSuccess, error: toastError } = useToast();
  const { data: rules, isLoading } = useTagRulesForTab(variant);
  const createRule = useCreateTagRuleForTab(variant);
  const updateRule = useUpdateTagRuleForTab(variant);
  const deleteRule = useDeleteTagRuleForTab(variant);
  const bulkDelete = useBulkDeleteTagRulesForTab(variant);
  const reorderRule = useReorderTagRulesForTab(variant);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const { scope, setScope } = useTagRuleScope();
  const inUseScopes = useMemo(() => overridesInUse(rules), [rules]);

  const grouped = useMemo(() => (rules ? groupRules(rules) : []), [rules]);

  const availableGroupKeys = useMemo(() => {
    if (!rules) return [];
    const keys = new Set<string>();
    for (const r of rules) {
      const k = (r.groupKey ?? '').trim();
      if (k) keys.add(k);
    }
    return [...keys].sort();
  }, [rules]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !rules) return;
      const group = grouped.find((g) => g.rules.some((r) => r.id === active.id));
      if (!group || group.rules.length < 2) return;
      const oldIndex = group.rules.findIndex((r) => r.id === active.id);
      const newIndex = group.rules.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(group.rules, oldIndex, newIndex);
      const updates = reordered.map((r, i) => ({
        id: r.id,
        orderIndex: reordered.length - 1 - i,
      }));
      try {
        await reorderRule.mutateAsync(updates);
        toastSuccess('Order updated', 'Rules reordered. Top = checked first.');
      } catch (e) {
        toastError('Reorder failed', getApiErrorMessage(e, 'Could not reorder rules'));
      }
    },
    [grouped, rules, reorderRule, toastSuccess, toastError],
  );

  const handleCreate = async (dto: CreateListTagRuleDto) => {
    try {
      await createRule.mutateAsync(dto);
      toastSuccess('Rule created', 'Your tag rule is active.');
      setModalOpen(false);
    } catch (e) {
      toastError('Create failed', getApiErrorMessage(e, 'Could not create rule'));
    }
  };

  const handleUpdate = async (dto: CreateListTagRuleDto) => {
    if (!editingId) return;
    try {
      await updateRule.mutateAsync({
        id: editingId,
        dto: {
          name: dto.name,
          color: dto.color,
          emoji: dto.emoji,
          singleSourceOnly: dto.singleSourceOnly,
          listSystemTags: dto.listSystemTags,
          conditions: dto.conditions,
          dateRangePreset: dto.dateRangePreset,
          fromDate: dto.fromDate,
          toDate: dto.toDate,
          toDateIsToday: dto.toDateIsToday,
          minVolume: dto.minVolume,
          enabled: dto.enabled,
          groupKey: dto.groupKey,
        },
      });
      toastSuccess('Rule updated', 'Changes saved.');
      setEditingId(null);
      setModalOpen(false);
    } catch (e) {
      toastError('Update failed', getApiErrorMessage(e, 'Could not update rule'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await deleteRule.mutateAsync(id);
      toastSuccess('Rule deleted', 'The rule was removed.');
    } catch (e) {
      toastError('Delete failed', getApiErrorMessage(e, 'Could not delete rule'));
    }
  };

  const handleToggleEnabled = async (rule: ListTagRule) => {
    try {
      await updateRule.mutateAsync({ id: rule.id, dto: { enabled: !rule.enabled } });
      toastSuccess(rule.enabled ? 'Rule disabled' : 'Rule enabled', 'Status updated.');
    } catch (e) {
      toastError('Update failed', getApiErrorMessage(e, 'Could not toggle'));
    }
  };

  const selectedCount = selectedIds.size;
  const allSelected = rules && rules.length > 0 && selectedCount === rules.length;

  const handleSelectAll = useCallback(() => {
    if (!rules?.length) return;
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rules.map((r) => r.id)));
    }
  }, [rules, allSelected]);

  const handleSelectRule = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedCount === 0) return;
    if (!window.confirm(`Delete ${selectedCount} selected rule${selectedCount !== 1 ? 's' : ''}?`)) return;
    try {
      const deleted = await bulkDelete.mutateAsync([...selectedIds]);
      toastSuccess('Rules deleted', `${deleted} rule${deleted !== 1 ? 's' : ''} removed.`);
      setSelectedIds(new Set());
    } catch (e) {
      toastError('Delete failed', getApiErrorMessage(e, 'Could not delete rules'));
    }
  }, [selectedCount, selectedIds, bulkDelete, toastSuccess, toastError]);

  const handleDeleteAll = useCallback(async () => {
    if (!rules?.length) return;
    if (!window.confirm(`Delete all ${rules.length} rules? This cannot be undone.`)) return;
    try {
      const ids = rules.map((r) => r.id);
      const deleted = await bulkDelete.mutateAsync(ids);
      toastSuccess('All rules deleted', `${deleted} rule${deleted !== 1 ? 's' : ''} removed.`);
      setSelectedIds(new Set());
    } catch (e) {
      toastError('Delete failed', getApiErrorMessage(e, 'Could not delete rules'));
    }
  }, [rules, bulkDelete, toastSuccess, toastError]);

  const handleCopyRules = useCallback(async () => {
    const toCopy = selectedCount > 0
      ? rules?.filter((r) => selectedIds.has(r.id)) ?? []
      : rules ?? [];
    if (toCopy.length === 0) {
      toastError('Nothing to copy', 'No rules selected. Select rules or copy all.');
      return;
    }
    const json = rulesToBulkImportJson(toCopy);
    try {
      await navigator.clipboard.writeText(json);
      toastSuccess(
        'Rules copied',
        `${toCopy.length} rule${toCopy.length !== 1 ? 's' : ''} copied. Paste into AI prompt or bulk import.`,
      );
    } catch {
      toastError('Copy failed', 'Could not copy to clipboard.');
    }
  }, [rules, selectedIds, selectedCount, toastSuccess, toastError]);

  const editingRule =
    editingId && rules ? rules.find((r) => r.id === editingId) ?? null : null;
  const showModal = modalOpen || editingId;

  return (
    <>
      <TagRuleScopeBar scope={scope} onScopeChange={setScope} inUse={inUseScopes} />
      {variant === 'hour' && <HourTagsNotice />}

      {/* Rules card - direct child of main div per plan DOM order */}
      <Card>
        <CardHeader className="border-b bg-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Layers className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Rules</CardTitle>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {rules?.length ?? 0} rule{rules?.length !== 1 ? 's' : ''} · All conditions must
                  pass (AND)
                </p>
              </div>
            </div>
            <RulesToolbar
              hasRules={Boolean(rules && rules.length > 0)}
              selectedCount={selectedCount}
              allSelected={Boolean(allSelected)}
              busy={bulkDelete.isPending}
              onSelectAll={handleSelectAll}
              onCopy={handleCopyRules}
              onDeleteSelected={handleDeleteSelected}
              onDeleteAll={handleDeleteAll}
              onNew={() => {
                setEditingId(null);
                setModalOpen(true);
              }}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : rules && rules.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <div className="divide-y">
                {grouped.map((group) => (
                  <div key={group.key} className="border-b border-border last:border-b-0">
                    <div className="border-b border-border/50 bg-muted/20 px-6 py-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </span>
                      {group.key !== UNGROUPED_KEY && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          · {group.rules.length} rule{group.rules.length !== 1 ? 's' : ''} · drag to
                          reorder (top = checked first)
                        </span>
                      )}
                    </div>
                    <SortableContext
                      items={group.rules.map((r) => r.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {group.rules.map((rule) => (
                        <SortableRuleRow
                          key={rule.id}
                          rule={rule}
                          onEdit={() => {
                            setEditingId(rule.id);
                            setModalOpen(true);
                          }}
                          onDelete={() => handleDelete(rule.id)}
                          onToggleEnabled={() => handleToggleEnabled(rule)}
                          isReorderPending={reorderRule.isPending}
                          selected={selectedIds.has(rule.id)}
                          onSelectChange={(checked) => handleSelectRule(rule.id, checked)}
                          scope={scope}
                        />
                      ))}
                    </SortableContext>
                  </div>
                ))}
              </div>
            </DndContext>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="rounded-full bg-muted p-5">
                <Tag className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-medium">No rules yet</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Create a rule to tag based on SMSWiser metrics. Example: &quot;High Profit&quot;
                when Profit ≥ 100.
              </p>
              <Button className="mt-6" onClick={() => setModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create first rule
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section with SystemTags (list only), BulkImport, Modal - next sibling per plan */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.35 }}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        {variant === 'list' && <SystemTagsSection />}
        {variant === 'hour' && <HourTagsPreview />}
        {variant !== 'list' && <VariantCacheSection variant={variant} />}
        <BulkImportSectionVariant variant={variant} />

        <ListTagRuleFormModal
          open={!!showModal}
          onOpenChange={(open) => {
            if (!open) {
              setModalOpen(false);
              setEditingId(null);
            }
          }}
          initial={editingRule}
          onSubmit={editingId ? handleUpdate : handleCreate}
          submitting={createRule.isPending || updateRule.isPending}
          availableGroupKeys={availableGroupKeys}
          scope={scope}
          variant={variant}
        />
      </motion.section>
    </>
  );
}
